/* eslint-disable react/require-optimization */
import * as React from "react";
import { QueryRenderer, graphql } from "react-relay";
import { type History } from "history";

import Environment from "~/lib/relay/environment";
import withHistory, { LocationHistoryContext } from "~/lib/withHistory";

import Panel from "~/components/shared/Panel";
import Button from "~/components/shared/Button";
import SectionLoader from "~/components/shared/SectionLoader";

import Jobs from "~/components/job/Index/Jobs";
import TimeRangeDropdown from "~/components/job/Index/TimeRangeDropdown";
import SearchInput from "~/components/job/Index/search-input";

// withHistory isn't a ts file, but I don't want to make it, so recreate the type
// of the LocationHistory context here.
type LocationHistoryContextType = React.Context<{ history: null | History }>;

type Props = {
  clusterQueueId?: string;
  organizationSlug: string;
  location: {
    query: {
      q?: string;
    };
  };
};

type State = {
  query: string | null | undefined;
  searchInputValue: string;
  createdAtFrom: string | null;
};

class JobIndex extends React.Component<Props, State> {
  environment = Environment.get();

  static query = graphql`
    query IndexJobIndexQuery($organizationSlug: ID!) {
      organization(slug: $organizationSlug) {
        name
        slug
        ...Jobs_organization
      }
    }
  `;

  static contextType = LocationHistoryContext;
  declare context: React.ContextType<LocationHistoryContextType>;

  constructor(initialProps: any) {
    super(initialProps);

    // Figure out if the default query
    const query =
      this.props.location.query.q !== undefined ? this.props.location.query.q : "state:scheduled";

    this.state = {
      query: query,
      searchInputValue: query,
      createdAtFrom: null,
    };
  }

  UNSAFE_componentWillReceiveProps(nextProps: Props) {
    // When the `q` param in the URL changes, do a new search
    const query = nextProps.location.query.q;
    if (query !== undefined && this.state.query !== query) {
      this.setState({ query: query, searchInputValue: query });
    }
  }

  get queryVariables() {
    return {
      organizationSlug: this.props.organizationSlug,
    };
  }

  render() {
    return (
      <QueryRenderer
        fetchPolicy="store-and-network"
        cacheConfig={{ force: true }}
        environment={this.environment}
        query={JobIndex.query}
        variables={this.queryVariables}
        render={this.renderQuery()}
      />
    );
  }

  renderQuery =
    () =>
    ({ error, props }) => {
      if (error) {
        return;
      }

      if (!props || !props.organization) {
        return <SectionLoader />;
      }

      return (
        <Panel>
          <Panel.Section className="my-2.5">
            <form onSubmit={this.handleFormSubmit} className="flex items-stretch gap-2">
              <SearchInput
                value={this.state.searchInputValue}
                onChange={this.handleSearchInputChange}
              />
              <TimeRangeDropdown onSelect={this.handleTimeRangeChange} />
              <Button theme="small">Search</Button>
            </form>

            <details className="dark-gray mt-1.5 text-sm">
              <summary className="cursor-pointer">
                Search for jobs {this.props.clusterQueueId ? "on this queue " : ""} by state,
                concurrency group or passed status.
              </summary>

              <div className="dark-gray mt-1.5 text-sm">
                Narrow down your search further by using one or more of the available search keys:
                <table className="frontend-table mt-2">
                  <tr>
                    <td valign="top" className="w-1/4 p-2">
                      <code className="code text-xs">state</code>
                    </td>
                    <td valign="top" className="w-3/4 p-2">
                      Filter jobs by their current state. Learn more about{" "}
                      <a
                        href="https://buildkite.com/docs/pipelines/defining-steps#job-states"
                        className="color-inherit text-purple-600 hover:underline"
                      >
                        job states.
                      </a>
                      <br />
                      <em>Example:</em>{" "}
                      <code className="code text-xs">state:scheduled,waiting</code>
                    </td>
                  </tr>
                  <tr>
                    <td valign="top" className="w-1/4 p-2">
                      <code className="code text-xs">concurrency-group</code>
                    </td>
                    <td valign="top" className="w-3/4 p-2">
                      Filter jobs by the concurrency group they belong to.
                      <br />
                      <em>Example:</em>{" "}
                      <code className="code text-xs">concurrency-group:concurrency-group-key</code>
                    </td>
                  </tr>
                  <tr>
                    <td valign="top" className="w-1/4 p-2">
                      <code className="code text-xs">passed</code>
                    </td>
                    <td valign="top" className="w-3/4 p-2">
                      Filter jobs based on whether they have passed or failed.
                      <br />
                      <em>Example:</em> <code className="code text-xs">passed:true/false</code>
                    </td>
                  </tr>
                </table>
              </div>
            </details>
          </Panel.Section>

          <Jobs
            query={this.state.query}
            clusterQueueId={this.props.clusterQueueId}
            organization={props.organization}
            createdAtFrom={this.state.createdAtFrom}
            onSuggestionClick={this.handleSuggestionClick}
          />
        </Panel>
      );
    };

  handleSearchInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({ searchInputValue: event.target.value });
  };

  handleTimeRangeChange = (time: string) => {
    this.setState({ createdAtFrom: time });
  };

  searchPath = (query: string) => {
    const url = new URL(window.location.href);
    url.searchParams.set("q", query);

    return url.pathname + url.search;
  };

  handleFormSubmit = (event: any) => {
    event.preventDefault();

    this.context.history?.push(this.searchPath(this.state.searchInputValue));
  };

  handleSuggestionClick = (suggestion: any) => {
    if (this.state.searchInputValue.includes(suggestion)) {
      return this.searchPath(this.state.searchInputValue);
    }

    const query = `${this.state.searchInputValue} ${suggestion}`;

    this.context.history?.push(this.searchPath(query));
  };
}

export default withHistory(JobIndex);
